import React, { useEffect, useState } from "react";
import { PlusIcon } from "evergreen-ui";
import { Alert, Button, Card, Col, Input, Modal, Row, Select, Table } from "antd";
import { invalidateDemoData, useListVals } from "../../../Firebase/databaseHooks";
import { getDatabase, ref, update } from "firebase/database";
import Firebase from "../../../Firebase/Firebase";
import FireFetch from "../../../Firebase/FireFetch";
import { useAuth } from "../../../AuthContext/AuthContext";
import TableActionMenu from "../../../Common/TableActionMenu";
import { getCallable } from "../../../Firebase/functionsClient";

const db = getDatabase(Firebase);
const listVolunteerTypesFn = getCallable("listVolunteerTypes");

const VolunteerLayout = ({ messageBack, canCreate = true, canUpdate = true, canDelete = true }) => {
	const { firebaseUser, loading: authLoading } = useAuth();
	const [volunteerArray, setVolunteerArray] = useState([]);
	const [volunteerLoading, setVolunteerLoading] = useState(false);
	const [volunteerError, setVolunteerError] = useState(null);
	const [langArray, langLoading] = useListVals(ref(db, "talmidsdb/languages"));

	const [showModal, setShowModal] = useState(false);
	const [showAlert, setShowAlert] = useState(false);
	const [alertInfo, setAlertInfo] = useState({
		color: "info",
		message: ""
	});

	const [formData, setFormData] = useState({
		name: '',
		code: '',
		language: null
	});

	const [selectedVolunteer, setSelectedVolunteer] = useState(null);
	const languageOptions = (langArray || [])
		.map((item) => ({
			value: item.id || item.languageID,
			label: item.languageName || item.language || item.id || item.languageID,
		}))
		.filter((item) => item.value && item.label);

	const resolveLanguageValue = (value) => {
		const raw = String(value || "").trim();
		if (!raw) return null;

		const matched = languageOptions.find((option) => {
			return (
				option.value === raw ||
				String(option.label).toLowerCase() === raw.toLowerCase()
			);
		});

		return matched?.value || raw;
	};

	const getLanguageLabel = (value) => {
		const resolved = resolveLanguageValue(value);
		const matched = languageOptions.find((option) => option.value === resolved);
		return matched?.label || value || "";
	};

	const columns = [
		{
			title: 'Worker Types',
			dataIndex: 'volunteerType',
			key: 'volunteerType',
			sorter: (a, b) => a.volunteerType?.localeCompare(b.volunteerType),
		},
		{
			title: 'Code',
			dataIndex: 'volunteerTypeCode',
			key: 'volunteerTypeCode',
			sorter: (a, b) => a.volunteerTypeCode?.localeCompare(b.volunteerTypeCode),
		},
		{
			title: 'Action',
			key: 'action',
			render: (_, record) => (
				<TableActionMenu
					items={[
						canUpdate && {
							key: "edit",
							label: "Edit",
							onClick: () => handleEdit(record),
						},
						canDelete && {
							key: "delete",
							label: "Delete",
							danger: true,
							onClick: () => {
								if (confirm("Delete this volunteer type?")) {
									messageBack("volunteers", "delete", record.id);
								}
							},
						},
					]}
				/>
			),
		}
	];

	const showAlertMessage = (message, color = "info") => {
		setAlertInfo({ message, color });
		setShowAlert(true);
		setTimeout(() => setShowAlert(false), 2000);
	};

	const handleEdit = (volunteer) => {
		if (!canUpdate) {
			showAlertMessage("You do not have permission to update worker types.", "danger");
			return;
		}

		setSelectedVolunteer(volunteer);
		setFormData({
			name: volunteer.volunteerType || "",
			code: volunteer.volunteerTypeCode || "",
			language: resolveLanguageValue(volunteer.language),
		});
		setShowModal(true);
	};

	const handleFormChange = (field, value) => {
		setFormData(prev => ({ ...prev, [field]: value }));
	};

	const handleUpdate = async () => {
		if (!canUpdate) {
			showAlertMessage("You do not have permission to update worker types.", "danger");
			return;
		}

		const { name, code, language } = formData;
		const trimmedName = String(name || "").trim();
		const trimmedCode = String(code || "").trim();
		const normalizedLanguage = resolveLanguageValue(language);

		if (!trimmedName || !trimmedCode || !normalizedLanguage) {
			showAlertMessage("Please fill all fields", "danger");
			return;
		}

		if (!selectedVolunteer?.id) {
			showAlertMessage("No volunteer selected", "danger");
			return;
		}

		const updateData = {
			id: selectedVolunteer.id,
			volunteerType: trimmedName,
			volunteerTypeCode: trimmedCode,
			language: normalizedLanguage,
			languageName: getLanguageLabel(normalizedLanguage),
		};

		try {
			await Promise.all([
				update(ref(db, `talmidsdb/volunteerTypes/${selectedVolunteer.id}`), updateData),
				update(ref(db, `talmidsdb/VolunteerTypes/${selectedVolunteer.id}`), updateData),
			]);
			invalidateDemoData([
				"talmidsdb/volunteerTypes",
				`talmidsdb/volunteerTypes/${selectedVolunteer.id}`,
				"talmidsdb/VolunteerTypes",
				`talmidsdb/VolunteerTypes/${selectedVolunteer.id}`,
			]);
			FireFetch.addLog("VolunteerTypes", selectedVolunteer.id, "Edit");
			setVolunteerArray((prev) =>
				prev.map((item) =>
					item.id === selectedVolunteer.id
						? { ...item, ...updateData }
						: item
				)
			);
			showAlertMessage("Volunteer type updated successfully", "success");
			setTimeout(() => setShowModal(false), 800);
		} catch (error) {
			showAlertMessage(`Unable to update: ${error.message}`, "danger");
		}
	};

	useEffect(() => {
		let isMounted = true;

		const loadVolunteerTypes = async () => {
			if (authLoading) return;
			if (!firebaseUser) {
				if (isMounted) {
					setVolunteerArray([]);
					setVolunteerError(new Error("Not authenticated"));
					setVolunteerLoading(false);
				}
				return;
			}

			setVolunteerLoading(true);
			setVolunteerError(null);

			try {
				const res = await listVolunteerTypesFn({});
				const types = Array.isArray(res?.data?.volunteerTypes) ? res.data.volunteerTypes : [];
				const formattedData = types.map((item, index) => ({
					key: item.id || index,
					...item,
					id: item.id || index,
				}));
				if (isMounted) {
					setVolunteerArray(formattedData);
				}
			} catch (error) {
				if (isMounted) {
					setVolunteerArray([]);
					setVolunteerError(error);
				}
			} finally {
				if (isMounted) {
					setVolunteerLoading(false);
				}
			}
		};

		loadVolunteerTypes();

		return () => {
			isMounted = false;
		};
	}, [authLoading, firebaseUser]);

	return (
		<div className="font-Nunito">
			<Modal
				open={showModal}
				title="Edit Worker Type"
				onCancel={() => setShowModal(false)}
				footer={null}
				width={900}
				destroyOnClose
			>
				<div className="d-flex flex-column">
					{showAlert && (
						<Alert
							type={alertInfo.color === "danger" ? "error" : alertInfo.color}
							message={alertInfo.message}
							className="my-3"
							showIcon
						/>
					)}
					{volunteerError && (
						<Alert
							type="error"
							message="Unable to load worker types. Please refresh and try again."
							className="my-3"
							showIcon
						/>
					)}

					<Row justify="center">
						<Col span={24}>
							<div className="d-flex flex-column mt-2">
								<label className="mb-1">Worker type name</label>
								<Input
									placeholder="Enter worker type name"
									value={formData.name}
									onChange={(e) => handleFormChange('name', e.target.value)}
								/>
							</div>

							<div className="d-flex flex-column my-2">
								<label className="mb-1">Worker type code</label>
								<Input
									placeholder="Enter worker type code"
									value={formData.code}
									onChange={(e) => handleFormChange('code', e.target.value)}
								/>
							</div>

							<div className="d-flex flex-column my-2">
								<label className="mb-1">Select language</label>
								<Select
									placeholder="Select Language"
									value={formData.language}
									showSearch
									options={languageOptions}
									optionFilterProp="label"
									filterOption={(input, option) =>
										String(option?.label || "").toLowerCase().includes(String(input || "").toLowerCase())
									}
									getPopupContainer={(triggerNode) => triggerNode.parentElement}
									onChange={(value) => handleFormChange('language', value)}
								/>
							</div>

							<Button
								type="primary"
								className="mt-3"
								onClick={handleUpdate}
							>
								Update
							</Button>
						</Col>
					</Row>
				</div>
			</Modal>

			{/* Main Content */}
			<div>
				{canCreate && (
					<Button
						type="primary"
						className="ml-3 my-2"
						onClick={() => messageBack("volunteers", "create")}
					>
						New Type <PlusIcon />
					</Button>
				)}
			</div>

			<Card className="d-flex flex-column">
				<Table
					style={{ overflow: "auto" }}
					loading={langLoading || volunteerLoading || authLoading}
					pagination={{
						defaultPageSize: 100,
						showSizeChanger: true,
						pageSizeOptions: ['20', '50', '100']
					}}
					dataSource={volunteerArray}
					columns={columns}
				/>
			</Card>
		</div>
	);
};

export default VolunteerLayout;
